import { useQuery } from '@tanstack/react-query';
import { useAppContext } from '@/context/AppContext';
import { useToken } from '@/lib/useToken';
import { apiFetch } from '@/lib/api';
import type { GroupDimension, GroupedReportResponse } from '@/types/reports';

export interface UseGroupedReportArgs {
  primary: GroupDimension;
  secondary: GroupDimension;
  /** Period query string in the same shape ReportsMonthlyPage builds — e.g.
   *  `month=2026-04` or `period=week&week=2026-W17`. Pass `null` to disable. */
  periodQs: string | null;
  clientId?: string;
  projectId?: string;
  employeeId?: string;
}

interface UseGroupedReportResult {
  data: GroupedReportResponse | null;
  isLoading: boolean;
}

const EMPTY_REPORT: GroupedReportResponse = {
  org: { id: '', name: '' },
  startDate: '',
  endDate: '',
  primary: 'date',
  secondary: 'project',
  filters: {},
  rows: [],
  summary: { billableHours: 0, nonBillableHours: 0, totalHours: 0 },
};

/**
 * Grouped time report data source — task 160.
 *
 * Fires the JSON endpoint with the configured primary/secondary dimensions,
 * the encoded period (reusing the monthly report's URL convention), and the
 * three optional narrowing filters. In mockup mode returns an empty report —
 * the page handles the empty-state rendering uniformly.
 */
export function useGroupedReport(args: UseGroupedReportArgs): UseGroupedReportResult {
  const { mockupMode, currentOrg } = useAppContext();
  const getToken = useToken();
  const orgId = currentOrg.id;
  const enabled = !mockupMode && !!orgId && !!args.periodQs && args.primary !== args.secondary;

  const query = useQuery({
    queryKey: ['groupedReport', orgId, args],
    queryFn: async () => {
      const token = await getToken();
      const params = new URLSearchParams(args.periodQs ?? '');
      params.set('primary', args.primary);
      params.set('secondary', args.secondary);
      if (args.clientId) params.set('clientId', args.clientId);
      if (args.projectId) params.set('projectId', args.projectId);
      if (args.employeeId) params.set('employeeId', args.employeeId);
      return apiFetch<GroupedReportResponse>(
        `/api/v1/orgs/${orgId}/reports/grouped?${params.toString()}`,
        token,
      );
    },
    enabled,
  });

  if (mockupMode) return { data: EMPTY_REPORT, isLoading: false };
  return { data: query.data ?? null, isLoading: query.isLoading };
}
